import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Upload, Plus, ChevronLeft, ChevronRight, RotateCcw, FileText, LayoutGrid, List as ListIcon, Zap } from "lucide-react";
import { listInvestors, fetchInvestorStats, createInvestor, bulkUpdateInvestors } from "../lib/api";
import {
  PHASES,
  BETREUER,
  EINSTUFUNGEN,
  INVESTORENTYP_KATEGORIEN,
} from "../components/investors/Badges";
import InvestorDrawer from "../components/investors/InvestorDrawer";
import ImportModal from "../components/investors/ImportModal";
import InvestorTable from "../components/investors/InvestorTable";
import InvestorAccordion from "../components/investors/InvestorAccordion";
import InvestorCardGrid from "../components/investors/InvestorCardGrid";
import BerichtModal from "../components/reports/BerichtModal";
import PageHeader from "../components/layout/PageHeader";

const VIEW_STORAGE_KEY = "invview.mode";  // "gruppen" | "liste" | "cards"
const SPEED_STORAGE_KEY = "invview.speedmode";

const QUELLEN_OPTIONS = [
  "Heinrichs-Recherche",
  "Gehring-Recherche",
  "BNP",
  "Listenchampion-MFO",
  "Listenchampion-SFO-Immobilien",
  "Listenchampion-Adel",
  "Listenchampion-Retail",
  "Heinrich-eigen",
];

export default function Investoren() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 50 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState(params.get("search") || "");
  const [phase, setPhase] = useState(params.get("phase") || "");
  const [quelle, setQuelle] = useState(params.get("quelle") || "");
  const [betreuer, setBetreuer] = useState(params.get("betreuer") || "");
  const [kategorie, setKategorie] = useState(params.get("investorentyp_kategorie") || "");
  const [einstufung, setEinstufung] = useState(params.get("einstufung") || "");
  const [gesperrtBnp, setGesperrtBnp] = useState(params.get("gesperrt_fuer_bnp") === "true");
  const [gesperrtUns, setGesperrtUns] = useState(params.get("gesperrt_fuer_uns") === "true");
  const [aBnp, setABnp] = useState(params.get("auch_bei_bnp") === "true");
  const [immo, setImmo] = useState(params.get("immobilien") === "true");
  const [ndaOffen, setNdaOffen] = useState(params.get("nda_offen") === "true");
  const [ebrAff, setEbrAff] = useState(params.get("ebr_aff") || "");
  const [lsAff, setLsAff]   = useState(params.get("ls_aff")  || "");
  const [deAff, setDeAff]   = useState(params.get("de_aff")  || "");
  const [page, setPage] = useState(Number(params.get("page") || 1));

  const [drawerId, setDrawerId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [berichtOpen, setBerichtOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  // View-Mode-Toggle: "gruppen" (default) zeigt Akkordion, "liste" die alte Tabelle.
  // Persistiert pro Browser via localStorage.
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem(VIEW_STORAGE_KEY) || "cards"; }
    catch { return "cards"; }
  });
  useEffect(() => {
    try { localStorage.setItem(VIEW_STORAGE_KEY, viewMode); } catch {
      /* localStorage nicht verfügbar — kein Drama, View-Mode bleibt nur im Memory */
    }
  }, [viewMode]);
  // Welle 5 — Speed-Modus (nur relevant für Card-View)
  const [speedMode, setSpeedMode] = useState(() => {
    try { return localStorage.getItem(SPEED_STORAGE_KEY) === "true"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(SPEED_STORAGE_KEY, String(speedMode)); } catch { /* ignore */ }
  }, [speedMode]);
  const [newFirma, setNewFirma] = useState("");
  const [newTier, setNewTier] = useState("");
  const [newEinstufung, setNewEinstufung] = useState("");
  const [newBetreuer, setNewBetreuer] = useState("Heinrichs");
  const [newQuelle, setNewQuelle] = useState("Heinrichs-Recherche");
  const [newEbrAff, setNewEbrAff] = useState("");
  const [newLsAff, setNewLsAff] = useState("");
  const [newDeAff, setNewDeAff] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState("");
  const [bulkValue, setBulkValue] = useState("");
  const [bulkRunning, setBulkRunning] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const q = {
        page,
        page_size: 50,
        sort_by: "tier",
        sort_dir: "asc",
        search: search || undefined,
        phase: phase || undefined,
        quelle: quelle || undefined,
        betreuer: betreuer || undefined,
        investorentyp_kategorie: kategorie || undefined,
        einstufung: einstufung || undefined,
        gesperrt_fuer_bnp: gesperrtBnp ? "true" : undefined,
        gesperrt_fuer_uns: gesperrtUns ? "true" : undefined,
        auch_bei_bnp: aBnp ? "true" : undefined,
        immobilien: immo ? "true" : undefined,
        nda_offen: ndaOffen ? "true" : undefined,
        ebr_affinitaet: ebrAff || undefined,
        ls_affinitaet: lsAff || undefined,
        de_affinitaet: deAff || undefined,
      };
      const [list, st] = await Promise.all([listInvestors(q), fetchInvestorStats()]);
      setData(list);
      setStats(st);
    } catch {
      toast.error("Liste konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [page, search, phase, quelle, betreuer, kategorie, einstufung, gesperrtBnp, gesperrtUns, aBnp, immo, ndaOffen, ebrAff, lsAff, deAff]);

  useEffect(() => { loadData(); }, [loadData]);

  // Reset Selection bei Filterwechsel
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, search, phase, quelle, betreuer, kategorie, einstufung, gesperrtBnp, gesperrtUns, aBnp, immo, ndaOffen, ebrAff, lsAff, deAff]);

  // URL-Sync
  useEffect(() => {
    const p = {};
    if (search) p.search = search;
    if (phase) p.phase = phase;
    if (quelle) p.quelle = quelle;
    if (betreuer) p.betreuer = betreuer;
    if (kategorie) p.investorentyp_kategorie = kategorie;
    if (einstufung) p.einstufung = einstufung;
    if (gesperrtBnp) p.gesperrt_fuer_bnp = "true";
    if (gesperrtUns) p.gesperrt_fuer_uns = "true";
    if (aBnp) p.auch_bei_bnp = "true";
    if (immo) p.immobilien = "true";
    if (ndaOffen) p.nda_offen = "true";
    if (ebrAff) p.ebr_aff = ebrAff;
    if (lsAff) p.ls_aff = lsAff;
    if (deAff) p.de_aff = deAff;
    if (page > 1) p.page = String(page);
    setParams(p, { replace: true });
  }, [search, phase, quelle, betreuer, kategorie, einstufung, gesperrtBnp, gesperrtUns, aBnp, immo, ndaOffen, ebrAff, lsAff, deAff, page, setParams]);

  const clearFilters = () => {
    setSearch(""); setPhase(""); setQuelle(""); setBetreuer(""); setKategorie(""); setEinstufung("");
    setGesperrtBnp(false); setGesperrtUns(false); setABnp(false); setImmo(false); setNdaOffen(false);
    setEbrAff(""); setLsAff(""); setDeAff(""); setPage(1);
  };
  const hasFilters = search || phase || quelle || betreuer || kategorie || einstufung || gesperrtBnp || gesperrtUns || aBnp || immo || ndaOffen || ebrAff || lsAff || deAff;

  const toggleSelect = (id) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = (checked) => {
    if (!checked) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(data.items.map((x) => x.id)));
    }
  };

  const applyBulk = async () => {
    if (selectedIds.size === 0 || !bulkAction) return;
    const updates = {};
    if (bulkAction === "phase") {
      if (!bulkValue) return toast.error("Phase wählen");
      updates.phase = bulkValue;
    } else if (bulkAction === "betreuer") {
      if (!bulkValue) return toast.error("Betreuer wählen");
      updates.betreuer_intern = bulkValue;
    } else if (bulkAction === "einstufung") {
      if (!bulkValue) return toast.error("Einstufung wählen");
      updates.einstufung = bulkValue;
    } else if (bulkAction === "sperre-bnp") {
      updates.gesperrt_fuer_bnp = bulkValue === "true";
    } else if (bulkAction === "sperre-uns") {
      updates.gesperrt_fuer_uns = bulkValue === "true";
    }
    setBulkRunning(true);
    try {
      const r = await bulkUpdateInvestors([...selectedIds], updates);
      toast.success(`${r.modified || 0} Investoren aktualisiert`);
      setSelectedIds(new Set()); setBulkAction(""); setBulkValue("");
      loadData();
    } catch {
      toast.error("Bulk-Update fehlgeschlagen");
    } finally {
      setBulkRunning(false);
    }
  };

  const handleCreate = async () => {
    if (!newFirma.trim()) return toast.error("Firma fehlt");
    try {
      const payload = { firma: newFirma.trim() };
      if (newTier) payload.tier = Number(newTier);
      if (newEinstufung) payload.einstufung = newEinstufung;
      if (newBetreuer) payload.betreuer_intern = newBetreuer;
      if (newQuelle) payload.quellen = [newQuelle];
      if (newEbrAff) payload.ebr_affinitaet = newEbrAff;
      if (newLsAff) payload.ls_affinitaet = newLsAff;
      if (newDeAff) payload.de_affinitaet = newDeAff;
      const inv = await createInvestor(payload);
      toast.success("Investor angelegt");
      setCreateOpen(false);
      setNewFirma(""); setNewTier(""); setNewEinstufung("");
      setNewBetreuer("Heinrichs"); setNewQuelle("Heinrichs-Recherche");
      setNewEbrAff(""); setNewLsAff(""); setNewDeAff("");
      setDrawerId(inv.id);
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anlegen fehlgeschlagen");
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));

  return (
    <div data-testid="investoren-page" className="pb-container px-0 sm:px-4 lg:px-8 py-4 sm:py-8 space-y-6">
      {/* Header */}
      <PageHeader
        eyebrow="Newton Campus · Pipeline"
        title="Investoren"
        subtitle="Alle Investoren der Akquise: BNP-Liste, Listenchampion-Pakete und Heinrichs-Recherche. Inline-Edit für Welle, Einstufung und Zuständigkeit."
        testid="investoren-header"
        actions={
          <>
            {/* View-Toggle: 3 Modi — Speed-Cards (Default) / Gruppen (Akkordion) / Liste (Tabelle) */}
            <div
              role="tablist"
              aria-label="Ansichtsmodus"
              className="inline-flex border border-navy/15 min-h-[40px]"
              data-testid="view-toggle"
            >
              <button
                role="tab"
                aria-selected={viewMode === "cards"}
                data-testid="view-toggle-cards"
                onClick={() => setViewMode("cards")}
                className={`px-3 text-[10px] uppercase tracking-widish font-sans flex items-center gap-1.5 ${
                  viewMode === "cards"
                    ? "bg-gold text-white"
                    : "bg-white text-navy/65 hover:text-navy"
                }`}
                title="Speed-Cards mit Quick-Actions"
              >
                <Zap size={12} /> <span className="hidden sm:inline">Cards</span>
              </button>
              <button
                role="tab"
                aria-selected={viewMode === "gruppen"}
                data-testid="view-toggle-gruppen"
                onClick={() => setViewMode("gruppen")}
                className={`px-3 text-[10px] uppercase tracking-widish font-sans flex items-center gap-1.5 border-l border-navy/15 ${
                  viewMode === "gruppen"
                    ? "bg-navy text-white"
                    : "bg-white text-navy/65 hover:text-navy"
                }`}
              >
                <LayoutGrid size={12} /> <span className="hidden sm:inline">Gruppen</span>
              </button>
              <button
                role="tab"
                aria-selected={viewMode === "liste"}
                data-testid="view-toggle-liste"
                onClick={() => setViewMode("liste")}
                className={`px-3 text-[10px] uppercase tracking-widish font-sans flex items-center gap-1.5 border-l border-navy/15 ${
                  viewMode === "liste"
                    ? "bg-navy text-white"
                    : "bg-white text-navy/65 hover:text-navy"
                }`}
              >
                <ListIcon size={12} /> <span className="hidden sm:inline">Liste</span>
              </button>
            </div>
            <button data-testid="open-bericht-btn" onClick={() => setBerichtOpen(true)} className="pb-btn-accent text-xs uppercase tracking-widish font-semibold min-h-[40px]" title="Excel-Komplettexport">
              <FileText size={13} /> <span className="hidden sm:inline">Investoren-Export</span><span className="sm:hidden">Export</span>
            </button>
            <button data-testid="open-import-btn" onClick={() => setImportOpen(true)} className="pb-btn-outline text-xs uppercase tracking-widish font-semibold min-h-[40px]" title="Excel-Import">
              <Upload size={13} /> <span className="hidden sm:inline">Excel-Import</span><span className="sm:hidden">Import</span>
            </button>
            <button data-testid="open-create-btn" onClick={() => setCreateOpen(true)} className="pb-btn-primary text-xs uppercase tracking-widish font-semibold min-h-[40px]" title="Neuer Investor">
              <Plus size={13} /> Neu
            </button>
          </>
        }
      />

      {viewMode === "cards" ? (
        // ----- SPEED-CARDS-ANSICHT (Welle 5) — mit Quick-Actions + Shortcuts -----
        <InvestorCardGrid
          onOpenDrawer={(id) => setDrawerId(id)}
          searchQuery={search}
          speedMode={speedMode}
          onToggleSpeed={setSpeedMode}
          groupBy="sweet_spot"
        />
      ) : viewMode === "gruppen" ? (
        // ----- GRUPPEN-ANSICHT (Phase 6.9) -----
        <InvestorAccordion
          onOpenDrawer={(id) => setDrawerId(id)}
          onSaved={() => { /* lokal optimistisch im Akkordion gehandhabt */ }}
        />
      ) : (
        <>
      {/* Filter-Bar */}
      <section className="pb-card space-y-4">
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 lg:col-span-3 relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-navy/40" />
            <input
              data-testid="filter-search"
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              placeholder="Suche Firma, E-Mail, Stadt…"
              className="pb-input--full pl-7"
            />
          </div>
          <select data-testid="filter-kategorie" value={kategorie} onChange={(e) => { setPage(1); setKategorie(e.target.value); }} className="pb-input--full col-span-6 lg:col-span-2">
            <option value="">Alle Investorentypen</option>
            {INVESTORENTYP_KATEGORIEN.map((k) => <option key={k}>{k}</option>)}
          </select>
          <select data-testid="filter-einstufung" value={einstufung} onChange={(e) => { setPage(1); setEinstufung(e.target.value); }} className="pb-input--full col-span-6 lg:col-span-2">
            <option value="">Alle Einstufungen</option>
            {EINSTUFUNGEN.map((e) => <option key={e}>{e === "Heiss" ? "Heiß" : e}</option>)}
          </select>
          <select data-testid="filter-phase" value={phase} onChange={(e) => { setPage(1); setPhase(e.target.value); }} className="pb-input--full col-span-6 lg:col-span-2">
            <option value="">Alle Phasen</option>
            {PHASES.map((p) => <option key={p}>{p}</option>)}
          </select>
          <select data-testid="filter-quelle" value={quelle} onChange={(e) => { setPage(1); setQuelle(e.target.value); }} className="pb-input--full col-span-6 lg:col-span-2">
            <option value="">Alle Herkünfte</option>
            {QUELLEN_OPTIONS.map((q) => <option key={q}>{q}</option>)}
          </select>
          <select data-testid="filter-betreuer" value={betreuer} onChange={(e) => { setPage(1); setBetreuer(e.target.value); }} className="pb-input--full col-span-6 lg:col-span-1">
            <option value="">Alle Zust.</option>
            {BETREUER.map((b) => <option key={b}>{b}</option>)}
          </select>
          <select data-testid="filter-ebr" value={ebrAff} onChange={(e) => { setPage(1); setEbrAff(e.target.value); }} className="pb-input--full col-span-6 lg:col-span-2" title="Erbbau-Affinität">
            <option value="">Erbbau-Affinität (alle)</option>
            <option value="hoch">EBR: hoch</option>
            <option value="mittel">EBR: mittel</option>
            <option value="niedrig">EBR: niedrig</option>
          </select>
          <select data-testid="filter-ls" value={lsAff} onChange={(e) => { setPage(1); setLsAff(e.target.value); }} className="pb-input--full col-span-6 lg:col-span-2" title="Life Science-Affinität">
            <option value="">Life Science (alle)</option>
            <option value="hoch">LS: hoch</option>
            <option value="mittel">LS: mittel</option>
            <option value="niedrig">LS: niedrig</option>
          </select>
          <select data-testid="filter-de" value={deAff} onChange={(e) => { setPage(1); setDeAff(e.target.value); }} className="pb-input--full col-span-6 lg:col-span-2" title="Deutschland-Affinität">
            <option value="">Deutschland (alle)</option>
            <option value="hoch">DE: hoch</option>
            <option value="mittel">DE: mittel</option>
            <option value="niedrig">DE: niedrig</option>
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-sans border-t border-navy/10 pt-3">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" data-testid="filter-gesperrt-bnp" checked={gesperrtBnp} onChange={(e) => { setPage(1); setGesperrtBnp(e.target.checked); }} className="accent-[#022750]"/>
            <span className="text-navy/75">Gesperrt für BNP</span>
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" data-testid="filter-gesperrt-uns" checked={gesperrtUns} onChange={(e) => { setPage(1); setGesperrtUns(e.target.checked); }} className="accent-[#022750]"/>
            <span className="text-navy/75">Gesperrt für uns</span>
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" data-testid="filter-auch-bnp" checked={aBnp} onChange={(e) => { setPage(1); setABnp(e.target.checked); }} className="accent-[#022750]"/>
            <span className="text-navy/75">Auch bei BNP</span>
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" data-testid="filter-immobilien" checked={immo} onChange={(e) => { setPage(1); setImmo(e.target.checked); }} className="accent-[#022750]"/>
            <span className="text-navy/75">Immobilien-Fokus</span>
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" data-testid="filter-nda-offen" checked={ndaOffen} onChange={(e) => { setPage(1); setNdaOffen(e.target.checked); }} className="accent-[#C4922A]"/>
            <span className="text-navy/75">NDA-Reminder offen (&gt;14T)</span>
          </label>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-navy/55 font-sans tabular-nums">
              <strong className="text-navy text-sm">{data.total}</strong> {stats ? `/ ${stats.total}` : ""}
            </span>
            {hasFilters && (
              <button data-testid="filter-reset" onClick={clearFilters} className="text-xs uppercase tracking-widish text-navy hover:text-gold inline-flex items-center gap-1.5 font-sans">
                <RotateCcw size={12} /> zurücksetzen
              </button>
            )}
          </div>
        </div>

        {selectedIds.size > 0 && (
          <div data-testid="bulk-action-bar" className="border-t border-navy/10 pt-3 flex items-center gap-3 flex-wrap">
            <span className="text-sm font-sans text-navy">
              <strong className="text-gold tabular-nums">{selectedIds.size}</strong> ausgewählt
            </span>
            <select
              data-testid="bulk-action-select"
              value={bulkAction}
              onChange={(e) => { setBulkAction(e.target.value); setBulkValue(""); }}
              className="pb-input--full text-xs w-auto"
            >
              <option value="">Aktion wählen…</option>
              <option value="phase">Phase setzen</option>
              <option value="einstufung">Einstufung setzen</option>
              <option value="betreuer">Betreuer setzen</option>
              <option value="sperre-bnp">Für BNP sperren</option>
              <option value="sperre-uns">Für uns sperren</option>
            </select>
            {bulkAction === "phase" && (
              <select data-testid="bulk-phase-value" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} className="pb-input--full text-xs w-auto">
                <option value="">Phase…</option>
                {PHASES.map((p) => <option key={p}>{p}</option>)}
              </select>
            )}
            {bulkAction === "einstufung" && (
              <select data-testid="bulk-einstufung-value" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} className="pb-input--full text-xs w-auto">
                <option value="">Einstufung…</option>
                {EINSTUFUNGEN.map((e) => <option key={e}>{e === "Heiss" ? "Heiß" : e}</option>)}
              </select>
            )}
            {bulkAction === "betreuer" && (
              <select data-testid="bulk-betreuer-value" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} className="pb-input--full text-xs w-auto">
                <option value="">Betreuer…</option>
                {BETREUER.map((b) => <option key={b}>{b}</option>)}
              </select>
            )}
            {(bulkAction === "sperre-bnp" || bulkAction === "sperre-uns") && (
              <select data-testid="bulk-sperre-value" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} className="pb-input--full text-xs w-auto">
                <option value="">…</option>
                <option value="true">sperren</option>
                <option value="false">entsperren</option>
              </select>
            )}
            <button data-testid="bulk-apply-btn" onClick={applyBulk} disabled={bulkRunning || !bulkAction} className="pb-btn-primary text-xs uppercase tracking-widish">
              {bulkRunning ? "läuft…" : "Anwenden"}
            </button>
            <button onClick={() => setSelectedIds(new Set())} className="pb-btn-ghost text-xs uppercase tracking-widish">
              Auswahl löschen
            </button>
          </div>
        )}
      </section>

      {/* Tabelle */}
      <section className="bg-white border border-navy/15">
        <InvestorTable
          testid="investoren-table"
          items={data.items}
          loading={loading}
          showTierColumn={true}
          selectable={true}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onToggleAll={toggleAll}
          onOpen={(id) => setDrawerId(id)}
          onSaved={(updated) => {
            setData((d) => ({
              ...d,
              items: d.items.map((x) => (x.id === updated.id ? updated : x)),
            }));
          }}
        />
      </section>

      {/* Pagination */}
      {data.total > data.page_size && (
        <div className="flex items-center justify-between text-xs font-sans">
          <span className="text-navy/60 tabular-nums">
            Seite <strong className="text-navy">{data.page}</strong> von <strong className="text-navy">{totalPages}</strong>
          </span>
          <div className="flex items-center gap-2">
            <button data-testid="page-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="pb-btn-ghost text-xs uppercase tracking-widish disabled:opacity-30">
              <ChevronLeft size={13} /> zurück
            </button>
            <button data-testid="page-next" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="pb-btn-ghost text-xs uppercase tracking-widish disabled:opacity-30">
              weiter <ChevronRight size={13} />
            </button>
          </div>
        </div>
      )}
        </>
      )}

      <InvestorDrawer
        investorId={drawerId}
        onClose={() => setDrawerId(null)}
        onChange={loadData}
      />
      <ImportModal open={importOpen} onClose={() => setImportOpen(false)} onImported={loadData} />
      <BerichtModal open={berichtOpen} onClose={() => setBerichtOpen(false)} />

      {createOpen && (
        <>
          <div onClick={() => setCreateOpen(false)} className="fixed inset-0 bg-navy/30 z-40" />
          <div data-testid="create-modal" className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(480px,95vw)] bg-white border border-navy/15 z-50">
            <div className="border-b border-navy/10 px-6 py-4">
              <div className="pb-eyebrow">Neuer Investor</div>
              <h3 className="font-display font-semibold text-navy text-xl mt-1 tracking-tight">Investor anlegen</h3>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="pb-label">Firma <span className="text-tot">*</span></label>
                <input
                  data-testid="create-firma-input"
                  value={newFirma}
                  onChange={(e) => setNewFirma(e.target.value)}
                  placeholder="z.B. ABC Capital GmbH"
                  className="pb-input--full"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="pb-label">Tier</label>
                  <select
                    data-testid="create-tier-select"
                    value={newTier}
                    onChange={(e) => setNewTier(e.target.value)}
                    className="pb-input--full"
                  >
                    <option value="">—</option>
                    <option value="1">T1 — Perfekter Match</option>
                    <option value="2">T2 — Stark</option>
                    <option value="3">T3 — Solide</option>
                    <option value="4">T4 — Weiter zu prüfen</option>
                    <option value="5">T5 — Long-Shot</option>
                    <option value="6">T6 — Nachrang</option>
                  </select>
                </div>
                <div>
                  <label className="pb-label">Einstufung</label>
                  <select
                    data-testid="create-einstufung-select"
                    value={newEinstufung}
                    onChange={(e) => setNewEinstufung(e.target.value)}
                    className="pb-input--full"
                  >
                    <option value="">—</option>
                    <option value="Heiss">Heiß</option>
                    <option value="Reaktivierbar">Reaktivierbar</option>
                    <option value="Nachrang">Nachrang</option>
                    <option value="Tot">Tot</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="pb-label">Betreuer</label>
                  <select
                    data-testid="create-betreuer-select"
                    value={newBetreuer}
                    onChange={(e) => setNewBetreuer(e.target.value)}
                    className="pb-input--full"
                  >
                    <option value="Heinrichs">Heinrichs</option>
                    <option value="Gehring">Gehring</option>
                    <option value="Kasper">Kasper</option>
                    <option value="Adldinger">Adldinger</option>
                    <option value="BNP">BNP</option>
                  </select>
                </div>
                <div>
                  <label className="pb-label">Quelle</label>
                  <select
                    data-testid="create-quelle-select"
                    value={newQuelle}
                    onChange={(e) => setNewQuelle(e.target.value)}
                    className="pb-input--full"
                  >
                    <option value="Heinrichs-Recherche">Heinrichs-Recherche</option>
                    <option value="Gehring-Recherche">Gehring-Recherche</option>
                    <option value="BNP">BNP</option>
                    <option value="LC-MFO">LC-MFO</option>
                    <option value="LC-SFO">LC-SFO</option>
                    <option value="LC-Adel">LC-Adel</option>
                    <option value="LC-Retail">LC-Retail</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="pb-label text-[10px]">EBR-Affinität</label>
                  <select
                    data-testid="create-ebr-select"
                    value={newEbrAff}
                    onChange={(e) => setNewEbrAff(e.target.value)}
                    className="pb-input--full text-xs"
                  >
                    <option value="">—</option>
                    <option value="hoch">hoch</option>
                    <option value="mittel">mittel</option>
                    <option value="niedrig">niedrig</option>
                  </select>
                </div>
                <div>
                  <label className="pb-label text-[10px]">LS-Affinität</label>
                  <select
                    data-testid="create-ls-select"
                    value={newLsAff}
                    onChange={(e) => setNewLsAff(e.target.value)}
                    className="pb-input--full text-xs"
                  >
                    <option value="">—</option>
                    <option value="hoch">hoch</option>
                    <option value="mittel">mittel</option>
                    <option value="niedrig">niedrig</option>
                  </select>
                </div>
                <div>
                  <label className="pb-label text-[10px]">DE-Affinität</label>
                  <select
                    data-testid="create-de-select"
                    value={newDeAff}
                    onChange={(e) => setNewDeAff(e.target.value)}
                    className="pb-input--full text-xs"
                  >
                    <option value="">—</option>
                    <option value="hoch">hoch</option>
                    <option value="mittel">mittel</option>
                    <option value="niedrig">niedrig</option>
                  </select>
                </div>
              </div>
              <div className="text-[11px] text-navy/55 font-sans pt-1 border-t border-navy/10">
                Schlüssel-Argument, Kommentar &amp; weitere Details kannst du danach im Drawer („Bearbeiten") setzen.
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy/10 flex justify-end gap-2">
              <button onClick={() => setCreateOpen(false)} className="pb-btn-ghost">Abbrechen</button>
              <button data-testid="create-submit-btn" onClick={handleCreate} className="pb-btn-primary">Anlegen</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
